import React, {
  forwardRef, useCallback, useEffect, useImperativeHandle, useLayoutEffect, useMemo, useRef, useState,
} from 'react';
import { flushSync } from 'react-dom';
import {
  Box, Button, Flex, IconButton, Input, Text, Textarea, useToast,
} from '@chakra-ui/react';
import {
  LuArrowUpDown, LuCalendar, LuCheck, LuChevronDown, LuChevronRight, LuMic, LuPlus,
  LuSparkles, LuTriangleAlert, LuX,
} from 'react-icons/lu';
import { useTranslation } from 'react-i18next';
import {
  deleteMovement, fetchGroupRows, getReferenceData, parseMovement, postMovement,
} from '../../api/endpointsBackend';
import { usePreferences } from '../../contexts/PreferencesContext';
import useMovementDraft from '../../hooks/useMovementDraft';
import useSheetHistory from '../../hooks/useSheetHistory';
import useKeyboardInset from '../../hooks/useKeyboardInset';
import usePhoneHomeCurrency from '../../hooks/usePhoneHomeCurrency';
import { amountToText, groupForDisplay } from '../../utils/amountParser';
import { formatFigure, formatMoney } from '../../utils/formatters';
import { todayLocalISO } from '../../utils/localDate';
import { isAiBudgetRefusal } from '../../utils/aiUsage';
import PhoneConfirmSheet from './PhoneConfirmSheet';
import { usePhoneShell } from './PhoneShellContext';
import {
  AccountIcon, AccountSheet, CategorySheet, CategoryTile, CurrencySheet, TagSheet, TileGrid,
  shortDayLabel, useImportsByItself,
} from './PhonePickers';
import {
  clearDraft, loadDraft, recallMovement, recentAccountIds, rememberAccount, rememberMovement, saveDraft,
} from './quickAddStorage';
import { loadPopularNotes, loadTopCategoryIds } from './quickAddLists';

// Above the shell's bars (1100), below Chakra's overlay and modal layers
// (1300, 1400), so the pickers and confirm sheets it opens sit over it.
const Z_INDEX = 1250;
const TILE_COUNT = 8;
const CHIP_COUNT = 8;
const UNDO_MS = 5000;
const DUPLICATE_WINDOW_MS = 10 * 60 * 1000;
// Below this much room above the keypad, the defaults line gives way.
const SHORT_SCREEN_PX = 400;
const NARROW = '@media screen and (max-width: 359.98px)';
const WIDE = '@media screen and (min-width: 360px)';
// What the parser answers when a sentence names no currency (its schema's
// default), so it is not taken as the user's word.
const PARSE_DEFAULT_CURRENCY = 'COP';
// The fields that show a ✨ when a description fills them.
const MARKED_FIELDS = [
  'type', 'amount', 'note', 'category_id', 'account_id', 'from_account_id', 'to_account_id', 'date', 'description',
];

const DESCRIBE_FOCUS = { borderColor: 'phone.link', boxShadow: '0 0 0 1px var(--chakra-colors-phone-link)' };

const TYPES = [
  { value: 'Expenses', labelKey: 'quickAdd.types.expense' },
  { value: 'Income', labelKey: 'quickAdd.types.income' },
  { value: 'Transfer', labelKey: 'quickAdd.types.transfer' },
];

function setInert(el, inert) {
  if (!el) return;
  // React 18.2 does not know the `inert` prop.
  if (inert) {
    el.setAttribute('inert', '');
    el.setAttribute('aria-hidden', 'true');
  } else {
    el.removeAttribute('inert');
    el.removeAttribute('aria-hidden');
  }
}

const noteIs = (note) => ({ entity: 'Movement', column: 'note', operator: 'eq', values: [note] });

/** The last movement with this name and type, for "as last time". */
async function lastMovementNamed(note, type) {
  const data = await fetchGroupRows({
    filters: [
      noteIs(note),
      { entity: 'Movement', column: 'type', operator: 'eq', values: [type] },
      { entity: 'Movement', column: 'is_ignored', operator: 'eq', values: [false] },
    ],
    scopes: [],
    orderBy: 'date_desc',
    limit: 1,
  });
  return data?.items?.[0] || null;
}

/**
 * A movement like this one created in the last 10 minutes. Create has no
 * idempotency key, so a save that timed out after the server committed would
 * be logged twice by a retry.
 */
async function recentDuplicate(payload) {
  const since = new Date(Date.now() - DUPLICATE_WINDOW_MS).toISOString();
  try {
    const data = await fetchGroupRows({
      filters: [
        noteIs(payload.note),
        { entity: 'Movement', column: 'amount', operator: 'eq', values: [payload.amount] },
        { entity: 'Movement', column: 'date', operator: 'eq', values: [payload.date] },
        { entity: 'Movement', column: 'created_at', operator: 'gte', values: [since] },
      ],
      scopes: [],
      orderBy: 'date_desc',
      limit: 1,
    });
    return data?.items?.[0] || null;
  } catch {
    return null;
  }
}

// A 4xx's detail is written for people; a 5xx's is an internal error.
const detailOf = (err) => {
  const status = err?.response?.status;
  const detail = status < 500 ? err?.response?.data?.detail : null;
  return typeof detail === 'string' && detail ? detail : null;
};

/** A button inside `root` a press has started on, while a field in `root` has focus. */
function pressKeepsFocus(root, target) {
  const active = document.activeElement;
  if (!root || !active || !root.contains(active)) return false;
  if (active.tagName !== 'INPUT' && active.tagName !== 'TEXTAREA') return false;
  const control = target?.closest?.('button, [role="button"], [role="radio"]');
  return Boolean(control && root.contains(control));
}

function blankDraft(options, currency) {
  const transfer = options.type === 'Transfer';
  return {
    type: options.type || 'Expenses',
    date: todayLocalISO(),
    amount: '',
    note: '',
    description: '',
    currency,
    account_id: transfer ? null : (options.accountId ?? null),
    category_id: null,
    from_account_id: transfer ? (options.accountId ?? null) : null,
    to_account_id: null,
    tag_movements: (options.tagIds || []).map((id) => ({ tag: { id } })),
    currency_exchange: null,
  };
}

/** The ✨ beside a value a description filled. */
function FilledMark({ show, size = 14 }) {
  const { t } = useTranslation('movements');
  if (!show) return null;
  return (
    <Box
      as={LuSparkles}
      boxSize={`${size}px`}
      color="phone.link"
      flexShrink={0}
      role="img"
      aria-label={t('quickAdd.describeMode.filledMark')}
    />
  );
}

/** A 44px pill: the name chips and the date chips. */
function Chip({ selected, children, onClick, ...rest }) {
  return (
    <Box
      as="button"
      type="button"
      onClick={onClick}
      display="inline-flex"
      alignItems="center"
      gap="6px"
      flexShrink={0}
      h="44px"
      px="16px"
      borderRadius="22px"
      bg={selected ? 'redesign.bg.accent' : 'redesign.bg.card'}
      border="1px solid"
      borderColor={selected ? 'phone.link' : 'redesign.border'}
      color="redesign.heading"
      fontSize="15px"
      whiteSpace="nowrap"
      aria-pressed={selected}
      _active={{ bg: 'redesign.bg.hover' }}
      {...rest}
    >
      {children}
    </Box>
  );
}

/**
 * A 56px row on a card: the label left, the value right, and a hint under
 * the label. The hint takes a line of its own and a long account name wraps
 * to a second line, so at 320px two accounts of one bank can still be told
 * apart.
 */
function FieldRow({ label, hint, value, onClick, mark }) {
  return (
    <Box
      as="button"
      type="button"
      onClick={onClick}
      display="grid"
      gridTemplateColumns="auto minmax(0, 1fr)"
      columnGap="10px"
      alignItems="center"
      w="100%"
      minH="56px"
      px="14px"
      py="6px"
      textAlign="left"
      bg="redesign.bg.card"
      border="1px solid"
      borderColor="redesign.border"
      borderRadius="12px"
      _active={{ bg: 'redesign.bg.hover' }}
    >
      <Text textStyle="phoneBody" color="phone.muted" whiteSpace="nowrap">{label}</Text>
      <Flex minW={0} justify="flex-end" align="center" gap="6px" color="redesign.heading">
        {value}
        <FilledMark show={mark} />
        <Box as={LuChevronRight} boxSize="18px" color="phone.muted" flexShrink={0} />
      </Flex>
      {hint && <Text gridColumn="1 / -1" textStyle="phoneMeta" color="phone.muted">{hint}</Text>}
    </Box>
  );
}

function AccountValue({ account, placeholder }) {
  if (!account) return <Text textStyle="phoneBody" color="phone.muted">{placeholder}</Text>;
  return (
    <>
      <Box color="phone.muted" flexShrink={0}><AccountIcon account={account} size={18} /></Box>
      <Text textStyle="phoneBody" noOfLines={2} minW={0} textAlign="right">{account.name}</Text>
    </>
  );
}

function DuplicateGuard() {
  const { t } = useTranslation('movements');
  return (
    <Flex gap="8px" align="flex-start" px="2px" mt="8px">
      <Box as={LuTriangleAlert} boxSize="16px" color="phone.alert" flexShrink={0} mt="1px" />
      <Text textStyle="phoneMeta" color="phone.muted">{t('quickAdd.duplicateGuard')}</Text>
    </Flex>
  );
}

/**
 * New movement, from the tab bar's +.
 *
 * Always mounted and parked below the screen with a transform, never hidden
 * with `display` or `visibility`: iOS raises the keypad only for a focus()
 * made inside the user's tap, so the amount field has to exist, and be
 * focusable, before the tap that opens the sheet. While parked it is `inert`.
 *
 * Because it is not a Chakra modal, it does what a modal would: it is a
 * labelled dialog, the page behind it (`contentRef`) is inert and does not
 * scroll while it is open, and closing it drops the keyboard.
 *
 * Amount first: the keypad is up when it opens, a popular name is one chip,
 * and a known name fills its category, account and currency "as last time".
 * The Save button names what is still missing instead of looking disabled.
 * ✨ Describe fills the same form from a sentence; nothing saves by itself.
 *
 * Opened through the ref: `open(options, { focus })`, where `options` is
 * `{ mode, describeText, type, accountId, tagIds }`.
 */
const QuickAddSheet = forwardRef(function QuickAddSheet({ contentRef }, ref) {
  const { t } = useTranslation(['movements', 'navigation', 'common', 'dates']);
  const toast = useToast();
  const { bumpRefresh, openMovement } = usePhoneShell();
  const { homeCurrency } = usePreferences();
  // The currency the rows print without a code: the toast's figure matches.
  const rowHome = usePhoneHomeCurrency();

  const sheetRef = useRef(null);
  const amountRef = useRef(null);
  const nameRef = useRef(null);
  const describeRef = useRef(null);
  const detailsRef = useRef(null);
  const resetTimer = useRef(null);
  // The default account is picked once the accounts have loaded.
  const defaultsPending = useRef(false);
  // The next save first looks for the movement having been saved already.
  const guardNext = useRef(false);
  // The name "as last time" last looked up, so it is not looked up again.
  const lookedUp = useRef('');
  // Which description read is current: opening, closing and leaving Describe
  // each start a new one, so an answer that arrives later is dropped.
  const parseRun = useRef(0);
  // The details scroll into view once the keypad has gone and they show.
  const detailsScrollPending = useRef(false);
  const savedTimer = useRef(null);

  const [isOpen, setIsOpen] = useState(false);
  const [mode, setMode] = useState('amount');
  const [refData, setRefData] = useState({ accounts: [], categories: [], currencies: [], tags: [] });
  const { accounts, categories, currencies, tags } = refData;
  const {
    draft, setDraft, resetDraft, isTouched, handleFieldChange, mergeParsed, buildMovementPayload,
    showExchangeRow, amountValue,
  } = useMovementDraft({ accounts });
  const importsByItself = useImportsByItself(isOpen);

  const [amountFocused, setAmountFocused] = useState(false);
  // A field in the details (the note, the amount received) has the keypad.
  const [detailsFocused, setDetailsFocused] = useState(false);
  const [exchangeFocused, setExchangeFocused] = useState(false);
  const [describeText, setDescribeText] = useState('');
  const [reading, setReading] = useState(false);
  const [describeError, setDescribeError] = useState(null);
  // Refused over the AI budget (MI-354): the error then points to the form.
  const [describeOverBudget, setDescribeOverBudget] = useState(false);
  // Fields a description filled (✨), and fields filled "as last time".
  const [marks, setMarks] = useState(() => new Set());
  const [lastTime, setLastTime] = useState(() => new Set());
  const [picker, setPicker] = useState(null);
  const [moreOpen, setMoreOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [failed, setFailed] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [maybeSaved, setMaybeSaved] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [notes, setNotes] = useState([]);
  const [topIds, setTopIds] = useState({});
  // "Saved · … Undo" after Save and add another, shown in the sheet: the
  // keypad stays up then, and would hide a toast at the bottom.
  const [justSaved, setJustSaved] = useState(null);

  const { inset, keyboardUp } = useKeyboardInset(isOpen);

  const type = draft?.type || 'Expenses';
  const isTransfer = type === 'Transfer';
  const name = (draft?.note || '').trim();
  const accountById = (id) => accounts.find((a) => a.id === id) || null;
  const category = categories.find((c) => c.id === draft?.category_id) || null;
  const account = accountById(draft?.account_id);
  const fromAccount = accountById(draft?.from_account_id);
  const toAccount = accountById(draft?.to_account_id);

  const isDirty = describeText.trim() !== '' || Boolean(draft && (
    String(draft.amount ?? '').trim()
    || name
    || (draft.description || '').trim()
    || (draft.category_id != null && isTouched('category_id'))
  ));

  useLayoutEffect(() => {
    setInert(sheetRef.current, true);
  }, []);

  // ---- open and close ---------------------------------------------------

  const clearState = useCallback(() => {
    setMode('amount');
    setDescribeText('');
    setDescribeError(null);
    setDescribeOverBudget(false);
    setReading(false);
    setMarks(new Set());
    setLastTime(new Set());
    setPicker(null);
    setMoreOpen(false);
    setFailed(false);
    setSaveError(null);
    setMaybeSaved(null);
    lookedUp.current = '';
  }, []);

  const clearSaved = useCallback(() => {
    clearTimeout(savedTimer.current);
    setJustSaved(null);
  }, []);

  const initialize = useCallback((options) => {
    clearTimeout(resetTimer.current);
    parseRun.current += 1;
    clearState();
    clearSaved();
    const specific = options.mode || options.type || options.accountId != null || options.tagIds?.length;
    // A plain + restores a draft that could not be saved.
    const saved = !specific && loadDraft();
    if (saved) {
      resetDraft({ ...saved.draft, date: saved.draft.date || todayLocalISO() });
      guardNext.current = Boolean(saved.guard);
      defaultsPending.current = false;
      lookedUp.current = (saved.draft.note || '').trim();
    } else {
      resetDraft(blankDraft(options, homeCurrency || 'COP'));
      guardNext.current = false;
      defaultsPending.current = options.accountId == null;
    }
    setMode(options.mode === 'describe' ? 'describe' : 'amount');
    if (options.describeText) setDescribeText(options.describeText);
  }, [clearState, clearSaved, resetDraft, homeCurrency]);

  const open = useCallback((options = {}, { focus = true } = {}) => {
    // Order matters on iOS: the field must be focusable before focus(), and
    // focus() must run inside the tap, before any re-render.
    setInert(sheetRef.current, false);
    if (focus && options.mode !== 'describe') {
      amountRef.current?.focus({ preventScroll: true });
    }
    initialize(options);
    setIsOpen(true);
  }, [initialize]);

  useImperativeHandle(ref, () => ({ open }), [open]);

  // The sheet sits outside any focus lock, so the confirm would open with the
  // keyboard still up over its buttons.
  const askDiscard = () => {
    if (sheetRef.current?.contains(document.activeElement)) document.activeElement.blur();
    setConfirmOpen(true);
  };

  const { close } = useSheetHistory('quick-add', isOpen, {
    onPop: () => {
      setIsOpen(false);
      parseRun.current += 1;
      // Emptied once it has slid away, not while it is still in view.
      resetTimer.current = setTimeout(() => {
        clearState();
        clearSaved();
        resetDraft(null);
      }, 350);
    },
    isDirty: () => isDirty,
    onDirtyPop: () => askDiscard(),
  });

  useEffect(() => {
    if (isOpen) {
      // open() already lifted it inside the tap; this covers an open from an
      // effect, which StrictMode's re-run of the mount effect would undo.
      setInert(sheetRef.current, false);
      const content = contentRef?.current;
      setInert(content, true);
      const previous = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = previous;
        setInert(content, false);
      };
    }
    // Drop the keyboard before parking the sheet, or it stays up over a
    // field nobody can see.
    if (sheetRef.current?.contains(document.activeElement)) document.activeElement.blur();
    setInert(sheetRef.current, true);
    return undefined;
  }, [isOpen, contentRef]);

  // Leaving the phone layout (a resize) unmounts the sheet: what was typed is
  // kept as the 24-hour draft, and the next + restores it.
  const latest = useRef({});
  latest.current = { isOpen, isDirty, draft };
  useEffect(() => () => {
    clearTimeout(resetTimer.current);
    clearTimeout(savedTimer.current);
    const { isOpen: wasOpen, isDirty: dirty, draft: last } = latest.current;
    if (wasOpen && dirty && last) saveDraft(last, { guard: guardNext.current });
  }, []);

  const requestClose = () => {
    if (isDirty) askDiscard();
    else close();
  };

  // Narrow desktop windows get the phone shell too; Escape closes quick add
  // there, as it would a modal. The confirm sheet and pickers handle their own.
  const requestCloseRef = useRef(requestClose);
  requestCloseRef.current = requestClose;
  useEffect(() => {
    if (!isOpen || confirmOpen || picker) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') requestCloseRef.current();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen, confirmOpen, picker]);

  // ---- data ---------------------------------------------------------------

  useEffect(() => {
    if (!isOpen) return undefined;
    let alive = true;
    getReferenceData()
      .then((data) => { if (alive) setRefData(data); })
      .catch(() => {});
    loadPopularNotes().then((list) => { if (alive) setNotes(list || []); });
    return () => { alive = false; };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || isTransfer || topIds[type]) return undefined;
    let alive = true;
    loadTopCategoryIds(type).then((ids) => {
      if (alive) setTopIds((prev) => ({ ...prev, [type]: ids }));
    });
    return () => { alive = false; };
  }, [isOpen, type, isTransfer, topIds]);

  // ---- fields -------------------------------------------------------------

  const unmark = useCallback((...fields) => {
    const drop = (prev) => {
      if (!fields.some((f) => prev.has(f))) return prev;
      const next = new Set(prev);
      fields.forEach((f) => next.delete(f));
      return next;
    };
    setMarks(drop);
    setLastTime(drop);
  }, []);

  const setField = (field, value) => {
    unmark(field);
    handleFieldChange(field, value);
  };

  // A picker is a sheet of its own, and no sheet opens with the keypad up.
  // The sheet keeps focus through a press (see `onMouseDown` below), so the
  // field is blurred here.
  const blurInside = () => {
    if (sheetRef.current?.contains(document.activeElement)) document.activeElement.blur();
  };
  const openPicker = (which) => {
    blurInside();
    setPicker(which);
  };

  /**
   * Choose an account for a leg. On the phone the currency follows the
   * account: one whose currencies lack the current one switches to its
   * first, so a movement is not born in a currency its account does not hold.
   */
  const chooseAccount = useCallback((field, acc, { touch = true } = {}) => {
    if (touch) unmark(field);
    handleFieldChange(field, acc?.id ?? null, { touch });
    const leads = field === 'account_id' || field === 'from_account_id';
    if (leads && acc?.currencies?.length) {
      setDraft((prev) => (acc.currencies.includes(prev.currency) ? prev : { ...prev, currency: acc.currencies[0] }));
    }
  }, [handleFieldChange, setDraft, unmark]);

  // Default account: the last one quick add used on this device, else the
  // first active cash account, else none.
  useEffect(() => {
    if (!isOpen || !defaultsPending.current || !accounts.length || !draft) return;
    defaultsPending.current = false;
    const active = accounts.filter((a) => a.is_active !== false);
    const last = recentAccountIds().map((id) => active.find((a) => a.id === id)).find(Boolean);
    const fallback = last || active.find((a) => a.account_type === 'cash');
    const field = draft.type === 'Transfer' ? 'from_account_id' : 'account_id';
    if (fallback && draft[field] == null) chooseAccount(field, fallback, { touch: false });
  }, [isOpen, accounts, draft, chooseAccount]);

  const changeType = (value) => {
    if (!draft || value === draft.type) return;
    unmark('type', 'category_id');
    // The account carries over between a purchase and a transfer's From leg,
    // untouched, so a later fill may still replace it.
    const carried = value === 'Transfer' ? draft.account_id : draft.type === 'Transfer' ? draft.from_account_id : null;
    handleFieldChange('type', value);
    if (carried != null) {
      handleFieldChange(value === 'Transfer' ? 'from_account_id' : 'account_id', carried, { touch: false });
    }
  };

  /**
   * "As last time": what this name was filed under last time, from this
   * device's memory first, then the last movement of that name. Each value
   * goes only into a field the user has not touched.
   */
  const recall = useCallback(async (note, forType) => {
    const trimmed = note.trim();
    if (!trimmed) return;
    lookedUp.current = trimmed;
    let found = recallMovement(forType, trimmed);
    if (!found) {
      const row = await lastMovementNamed(trimmed, forType).catch(() => null);
      if (row) {
        found = {
          category_id: row.category_id,
          account_id: row.account_id,
          from_account_id: row.from_account_id,
          to_account_id: row.to_account_id,
          currency: row.currency,
        };
      }
    }
    // The name changed while this was being looked up.
    if (!found || lookedUp.current !== trimmed) return;
    const usable = (id) => (id != null && accounts.some((a) => a.id === id && a.is_active !== false) ? id : null);
    const values = forType === 'Transfer'
      ? { from_account_id: usable(found.from_account_id), to_account_id: usable(found.to_account_id) }
      : {
        account_id: usable(found.account_id),
        category_id: categories.some((c) => c.id === found.category_id && c.type === forType) ? found.category_id : null,
      };
    values.currency = found.currency || null;
    setLastTime(new Set(mergeParsed(values)));
  }, [accounts, categories, mergeParsed]);

  // A typed name that matches an earlier one fills in too, once typing pauses.
  useEffect(() => {
    if (!isOpen || mode !== 'amount' || !name || name === lookedUp.current) return undefined;
    const id = setTimeout(() => recall(name, type), 600);
    return () => clearTimeout(id);
  }, [isOpen, mode, name, type, recall]);

  const pickNote = (note) => {
    setField('note', note);
    document.activeElement?.blur();
    recall(note, type);
  };

  // Up to 8 popular names of this type, not a category's name (a nameless
  // movement is saved under its category's name), narrowed by what is typed.
  const chips = useMemo(() => {
    const categoryNames = new Set(categories.map((c) => c.name.toLowerCase()));
    const typed = name.toLowerCase();
    return notes
      .filter((n) => n.type === type && n.note && !categoryNames.has(n.note.toLowerCase()))
      .map((n) => n.note)
      .filter((note) => note.toLowerCase().includes(typed) && note.toLowerCase() !== typed)
      .slice(0, CHIP_COUNT);
  }, [notes, categories, type, name]);

  const tiles = useMemo(() => {
    if (isTransfer) return [];
    const ofType = categories.filter((c) => c.type === type);
    const top = (topIds[type] || []).map((id) => ofType.find((c) => c.id === id)).filter(Boolean);
    const rest = ofType.filter((c) => !top.includes(c)).sort((a, b) => a.name.localeCompare(b.name));
    const list = [...top, ...rest].slice(0, TILE_COUNT);
    // A category picked from "All categories" stays in view.
    if (category && category.type === type && !list.includes(category)) list[list.length - 1] = category;
    return list;
  }, [categories, topIds, type, isTransfer, category]);

  // "Amount received" is typed in the To account's currency.
  const exchangeTarget = useMemo(() => {
    const list = toAccount?.currencies || [];
    return list.find((c) => c !== draft?.currency) || list[0] || null;
  }, [toAccount, draft?.currency]);

  const setExchange = (text) => {
    setDraft((prev) => ({
      ...prev,
      currency_exchange: { currency_target: exchangeTarget, value_target: text.replace(/[^0-9.,]/g, '') },
    }));
  };

  const swapLegs = () => {
    unmark('from_account_id', 'to_account_id');
    handleFieldChange('from_account_id', draft.to_account_id);
    handleFieldChange('to_account_id', draft.from_account_id);
    if (toAccount?.currencies?.length && !toAccount.currencies.includes(draft.currency)) {
      setDraft((prev) => ({ ...prev, currency: toAccount.currencies[0] }));
    }
  };

  const tagIds = (draft?.tag_movements || []).map((tm) => tm.tag.id);
  const tagNames = (draft?.tag_movements || [])
    .map((tm) => tags.find((tag) => tag.id === tm.tag.id)?.name || tm.tag.name)
    .filter(Boolean);

  // ---- saving -------------------------------------------------------------

  // What stops a save, if anything: the Save button says it.
  let blocker = null;
  if (!(amountValue > 0)) blocker = 'enterAmount';
  else if (isTransfer && draft.from_account_id == null && draft.to_account_id == null) blocker = 'chooseAccount';
  else if (isTransfer && !name) blocker = 'addName';
  else if (!isTransfer && !name && !category) blocker = 'addNameOrCategory';

  let hint = null;
  if (blocker === 'addName') hint = t('quickAdd.hints.transferNoName');
  else if (!blocker && !isTransfer && !category) hint = t('quickAdd.hints.noCategory');
  else if (!blocker && !isTransfer && !account) hint = t('quickAdd.hints.noAccount');

  let saveLabel;
  if (blocker) saveLabel = t(`quickAdd.save.${blocker}`);
  else if (failed) saveLabel = t('quickAdd.save.retry');
  else saveLabel = t(`quickAdd.save.${isTransfer ? 'transfer' : type === 'Income' ? 'income' : 'expense'}`);

  const focusMissing = () => {
    if (blocker === 'enterAmount') amountRef.current?.focus();
    else if (blocker === 'chooseAccount') openPicker('from');
    else nameRef.current?.focus();
  };

  // As the rows print it, with the code even for the home currency:
  // "−COP 32,900", "−USD 8.00".
  const amountLabel = (payload) => {
    const sign = payload.type === 'Expenses' ? '−' : payload.type === 'Income' ? '+' : '';
    const figure = payload.currency && payload.currency !== rowHome
      ? formatMoney(Math.abs(payload.amount), payload.currency, true)
      : formatFigure(payload.amount);
    return `${sign}${payload.currency} ${figure}`;
  };

  // Returns whether the movement was deleted.
  const undo = async (id, { quiet = false } = {}) => {
    try {
      await deleteMovement(id);
      bumpRefresh();
      if (!quiet) toast({ title: t('quickAdd.undone'), status: 'info', duration: 2500 });
      return true;
    } catch {
      toast({ title: t('quickAdd.undoFailed'), status: 'error', duration: 4000 });
      return false;
    }
  };

  /**
   * "Saved · Macpollo −COP 32,900" and Undo on one line. Only the name
   * truncates: the amount is what confirms the save.
   */
  const savedLine = ({ name: savedName, amount, onUndo }) => (
    <>
      <Box as={LuCheck} boxSize="18px" color="phone.income" flexShrink={0} />
      <Flex flex="1" minW={0} align="baseline" gap="5px" textStyle="phoneBody">
        <Box as="span" minW={0} whiteSpace="nowrap" overflow="hidden" textOverflow="ellipsis">
          {t('quickAdd.savedName', { name: savedName })}
        </Box>
        <Box as="span" flexShrink={0} whiteSpace="nowrap" sx={{ fontVariantNumeric: 'tabular-nums' }}>{amount}</Box>
      </Flex>
      <Button
        variant="ghost"
        color="phone.link"
        fontSize="17px"
        fontWeight="600"
        px="12px"
        flexShrink={0}
        onClick={onUndo}
      >
        {t('quickAdd.undo')}
      </Button>
    </>
  );

  // With Undo, for five seconds. Chakra's toast region is its own live region.
  const showSavedToast = (created, payload) => {
    const until = Date.now() + UNDO_MS;
    toast({
      duration: UNDO_MS,
      render: ({ onClose }) => (
        <Flex
          align="center"
          gap="10px"
          minH="52px"
          pl="16px"
          pr="6px"
          borderRadius="14px"
          bg="redesign.bg.popup"
          border="1px solid"
          borderColor="redesign.border"
          boxShadow="lg"
          color="redesign.heading"
        >
          {savedLine({
            name: payload.note,
            amount: amountLabel(payload),
            onUndo: () => {
              onClose();
              if (Date.now() <= until && created?.id) undo(created.id);
            },
          })}
        </Flex>
      ),
    });
  };

  // The same line inside the sheet, above Save, after Save and add another.
  const showSavedInSheet = (created, payload) => {
    clearTimeout(savedTimer.current);
    setJustSaved({ id: created?.id, name: payload.note, amount: amountLabel(payload) });
    savedTimer.current = setTimeout(() => setJustSaved(null), UNDO_MS);
  };

  const undoInSheet = async () => {
    const id = justSaved?.id;
    clearSaved();
    if (!id) return;
    if (await undo(id, { quiet: true })) {
      setJustSaved({ undone: true });
      savedTimer.current = setTimeout(() => setJustSaved(null), 2500);
    }
  };

  const save = async ({ another = false, skipGuard = false } = {}) => {
    if (saving) return;
    if (blocker) { focusMissing(); return; }
    // Before any await: iOS keeps the keypad up only for a focus() made
    // inside the tap.
    if (another) amountRef.current?.focus({ preventScroll: true });

    const payload = buildMovementPayload();
    // The backend needs a name; a nameless purchase is saved under its
    // category's name.
    if (!name && category) payload.note = category.name;
    if (tagIds.length) payload.tag_ids = tagIds;

    setSaving(true);
    setSaveError(null);
    setMaybeSaved(null);
    try {
      if (guardNext.current && !skipGuard) {
        const existing = await recentDuplicate(payload);
        if (existing) {
          setMaybeSaved({ id: existing.id, another });
          return;
        }
      }
      const created = await postMovement(payload);
      guardNext.current = false;
      setFailed(false);
      clearDraft();
      rememberAccount(isTransfer ? (payload.from_account_id ?? payload.to_account_id) : payload.account_id);
      if (name) {
        rememberMovement(payload.type, name, {
          category_id: payload.category_id ?? null,
          account_id: payload.account_id ?? null,
          from_account_id: payload.from_account_id ?? null,
          to_account_id: payload.to_account_id ?? null,
          currency: payload.currency,
        });
      }
      bumpRefresh();
      if (another) {
        showSavedInSheet(created, payload);
        // Type, account, date and currency stay; the rest starts over.
        resetDraft(
          { ...draft, amount: '', note: '', category_id: null, description: '', currency_exchange: null },
          ['type', 'account_id', 'from_account_id', 'to_account_id', 'date', 'currency', 'tag_movements'],
        );
        clearState();
      } else {
        showSavedToast(created, payload);
        await close();
      }
    } catch (err) {
      const status = err?.response?.status;
      if (!err?.response || status >= 500) {
        // It may have been saved anyway: the next try looks first.
        setFailed(true);
        guardNext.current = true;
        saveDraft(draft, { guard: true });
      } else {
        setSaveError(detailOf(err) || t('quickAdd.saveError'));
      }
    } finally {
      setSaving(false);
    }
  };

  const openExisting = async () => {
    const id = maybeSaved?.id;
    clearDraft();
    guardNext.current = false;
    setMaybeSaved(null);
    await close();
    if (id) openMovement(id);
  };

  // ---- describe -----------------------------------------------------------

  const toDescribe = () => {
    flushSync(() => setMode('describe'));
    describeRef.current?.focus();
  };

  const toAmountMode = () => {
    // A read still running is abandoned: the user is typing the form now.
    parseRun.current += 1;
    setReading(false);
    flushSync(() => setMode('amount'));
    amountRef.current?.focus({ preventScroll: true });
  };

  /**
   * The currency follows the account here too, as `chooseAccount` has it,
   * unless the sentence stated one: a parse always names a currency, and its
   * default is not the user's word.
   */
  const followAccountCurrency = (stated) => {
    setDraft((prev) => {
      if (!prev) return prev;
      const lead = accountById(prev.type === 'Transfer' ? prev.from_account_id : prev.account_id);
      if (!lead?.currencies?.length || lead.currencies.includes(prev.currency) || stated) return prev;
      return { ...prev, currency: lead.currencies[0] };
    });
  };

  const fillIn = async () => {
    const text = describeText.trim();
    if (reading) return;
    if (!text) {
      describeRef.current?.focus();
      return;
    }
    parseRun.current += 1;
    const run = parseRun.current;
    setReading(true);
    setDescribeError(null);
    setDescribeOverBudget(false);
    try {
      const parsed = await parseMovement(text);
      // Closed, reopened or switched to the form while this was reading.
      if (run !== parseRun.current) return;
      const filled = mergeParsed(parsed, { carryAccount: true });
      const stated = isTouched('currency')
        || (filled.includes('currency') && parsed.currency !== PARSE_DEFAULT_CURRENCY);
      followAccountCurrency(stated);
      // The parsed name is not looked up "as last time": that would replace
      // what the description said.
      lookedUp.current = (parsed?.note || '').trim();
      setMarks(new Set(filled.filter((key) => MARKED_FIELDS.includes(key))));
      setLastTime(new Set());
      blurInside();
      setMode('amount');
    } catch (err) {
      if (run !== parseRun.current) return;
      setDescribeError(detailOf(err) || t('quickAdd.describeMode.error'));
      setDescribeOverBudget(isAiBudgetRefusal(err));
    } finally {
      if (run === parseRun.current) setReading(false);
    }
  };

  // ---- render -------------------------------------------------------------

  const viewportHeight = typeof window !== 'undefined'
    ? (window.visualViewport?.height ?? window.innerHeight) : 0;
  const shortScreen = keyboardUp && viewportHeight < SHORT_SCREEN_PX;
  // With the keypad up the details give way to the next targets (the name and
  // its chips), and this line says what will be saved without them. On a
  // short screen the line, the hint above Save and the type give way too.
  const hideDetails = mode === 'amount' && keyboardUp && !detailsFocused;
  const showDefaultsLine = hideDetails && !shortScreen;

  useEffect(() => {
    if (hideDetails || !detailsScrollPending.current) return;
    detailsScrollPending.current = false;
    detailsRef.current?.scrollIntoView({ block: 'start', behavior: 'smooth' });
  }, [hideDetails]);

  const amountText = typeof draft?.amount === 'number' ? amountToText(draft.amount) : (draft?.amount ?? '');
  const exchangeText = draft?.currency_exchange?.value_target ?? '';
  const today = todayLocalISO();
  const now = new Date();
  const yesterday = todayLocalISO(new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1));
  const date = draft?.date || today;
  const otherDate = date !== today && date !== yesterday;
  let dateLabel = shortDayLabel(date, t);
  if (date === today) dateLabel = t('quickAdd.dates.today');
  else if (date === yesterday) dateLabel = t('quickAdd.dates.yesterday');

  const defaultsLine = [
    (isTransfer ? fromAccount : account)?.name || t('quickAdd.defaults.noAccount'),
    isTransfer ? null : (category?.name || t('quickAdd.defaults.noCategory')),
    dateLabel,
  ].filter(Boolean).join(' · ');

  // The keypad drops first; the details show, then scroll into view.
  const showDetails = () => {
    detailsScrollPending.current = true;
    blurInside();
  };

  const accountLabel = t(type === 'Income' ? 'quickAdd.receivedIn' : 'quickAdd.paidWith');

  const accountField = isTransfer ? null : (
    <>
      <FieldRow
        label={accountLabel}
        hint={lastTime.has('account_id') ? t('quickAdd.asLastTime') : null}
        value={<AccountValue account={account} placeholder={t('quickAdd.choose')} />}
        mark={marks.has('account_id')}
        onClick={() => openPicker('account')}
      />
      {importsByItself(account) && <DuplicateGuard />}
    </>
  );

  const transferFields = !isTransfer ? null : (
    <>
      <Flex align="center" gap="8px">
        <Box flex="1" minW={0}>
          <FieldRow
            label={t('quickAdd.from')}
            hint={lastTime.has('from_account_id') ? t('quickAdd.asLastTime') : null}
            value={<AccountValue account={fromAccount} placeholder={t('quickAdd.choose')} />}
            mark={marks.has('from_account_id')}
            onClick={() => openPicker('from')}
          />
          <Box h="8px" />
          <FieldRow
            label={t('quickAdd.to')}
            hint={lastTime.has('to_account_id') ? t('quickAdd.asLastTime') : null}
            value={<AccountValue account={toAccount} placeholder={t('quickAdd.choose')} />}
            mark={marks.has('to_account_id')}
            onClick={() => openPicker('to')}
          />
        </Box>
        <IconButton
          icon={<Box as={LuArrowUpDown} boxSize="20px" />}
          variant="ghost"
          color="phone.link"
          flexShrink={0}
          aria-label={t('quickAdd.swap')}
          onClick={swapLegs}
        />
      </Flex>
      {importsByItself(fromAccount) && <DuplicateGuard />}
      {showExchangeRow && exchangeTarget && (
        <Flex
          as="label"
          align="center"
          gap="10px"
          mt="8px"
          minH="52px"
          px="14px"
          bg="redesign.bg.card"
          border="1px solid"
          borderColor="redesign.border"
          borderRadius="12px"
        >
          <Text textStyle="phoneBody" color="redesign.text" flexShrink={0}>
            {t('quickAdd.amountReceived', { currency: exchangeTarget })}
          </Text>
          <Input
            type="text"
            inputMode="decimal"
            autoComplete="off"
            variant="unstyled"
            textAlign="right"
            fontWeight="600"
            color="redesign.heading"
            placeholder="0"
            value={exchangeFocused ? exchangeText : groupForDisplay(exchangeText)}
            onFocus={() => setExchangeFocused(true)}
            onBlur={() => setExchangeFocused(false)}
            onChange={(e) => setExchange(e.target.value)}
          />
        </Flex>
      )}
    </>
  );

  const categoryField = isTransfer ? null : (
    <Box mt="16px">
      <Flex align="baseline" justify="space-between" mb="8px" px="2px">
        <Text textStyle="phoneCaption" color="phone.muted">{t('quickAdd.category')}</Text>
        {lastTime.has('category_id') && (
          <Text textStyle="phoneMeta" color="phone.muted">{t('quickAdd.asLastTime')}</Text>
        )}
      </Flex>
      <Box mx="-16px">
        <TileGrid>
          {tiles.map((c) => (
            <CategoryTile
              key={c.id}
              plain
              category={c}
              selected={c.id === draft?.category_id}
              mark={marks.has('category_id') && c.id === draft?.category_id ? <FilledMark show size={12} /> : null}
              onClick={() => setField('category_id', c.id === draft?.category_id ? null : c.id)}
            />
          ))}
        </TileGrid>
      </Box>
      <Button
        variant="ghost"
        color="phone.link"
        fontSize="16px"
        fontWeight="500"
        px="2px"
        mt="4px"
        h="44px"
        rightIcon={<Box as={LuChevronRight} boxSize="16px" />}
        onClick={() => openPicker('category')}
      >
        {t('quickAdd.allCategories')}
      </Button>
    </Box>
  );

  const dateField = (
    <Flex gap="8px" mt={isTransfer ? '16px' : '4px'} flexWrap="wrap">
      <Chip selected={date === today} onClick={() => setField('date', today)}>
        {t('quickAdd.dates.today')}
        <FilledMark show={marks.has('date') && date === today} size={13} />
      </Chip>
      <Chip selected={date === yesterday} onClick={() => setField('date', yesterday)}>
        {t('quickAdd.dates.yesterday')}
        <FilledMark show={marks.has('date') && date === yesterday} size={13} />
      </Chip>
      <Box position="relative" flexShrink={0}>
        <Chip as="div" selected={otherDate} aria-hidden="true">
          <Box as={LuCalendar} boxSize="15px" color="phone.muted" />
          {otherDate ? dateLabel : t('quickAdd.dates.other')}
          <FilledMark show={marks.has('date') && otherDate} size={13} />
        </Chip>
        {/* The native picker (the iOS wheel), laid over the chip. */}
        <Input
          type="date"
          position="absolute"
          inset={0}
          w="100%"
          h="44px"
          opacity={0}
          cursor="pointer"
          aria-label={t('quickAdd.dates.other')}
          value={date}
          onClick={(e) => { try { e.currentTarget.showPicker?.(); } catch { /* not in a gesture */ } }}
          onChange={(e) => { if (e.target.value) setField('date', e.target.value); }}
        />
      </Box>
    </Flex>
  );

  const tagsText = tagNames.map((n) => `#${n}`).join(' ');
  const moreDetails = (
    <Box mt="8px">
      <Box
        as="button"
        type="button"
        onClick={() => setMoreOpen((v) => !v)}
        display="flex"
        alignItems="center"
        w="100%"
        minH="44px"
        px="2px"
        aria-expanded={moreOpen}
      >
        <Text textStyle="phoneBody" color="redesign.text">{t('quickAdd.moreDetails')}</Text>
        <Box as={LuChevronDown} boxSize="16px" ml="4px" color="phone.muted" transform={moreOpen ? 'rotate(180deg)' : undefined} />
        <Flex flex="1" minW={0} justify="flex-end" align="center" gap="4px">
          <Text textStyle="phoneMeta" color="phone.muted" noOfLines={1}>
            {tagsText || t('quickAdd.moreDetailsSummary')}
          </Text>
          <FilledMark show={marks.has('description')} />
        </Flex>
      </Box>
      {moreOpen && (
        <Box mt="4px">
          <FieldRow
            label={t('quickAdd.tags')}
            value={(
              <Text textStyle="phoneBody" color={tagsText ? 'redesign.heading' : 'phone.muted'} noOfLines={1}>
                {tagsText || t('quickAdd.addTags')}
              </Text>
            )}
            onClick={() => openPicker('tags')}
          />
          <Box position="relative" mt="8px">
            <Textarea
              rows={2}
              borderRadius="12px"
              bg="redesign.bg.card"
              borderColor="redesign.border"
              placeholder={t('quickAdd.notePlaceholder')}
              aria-label={t('quickAdd.note')}
              // A note may take several lines: Return is a new line.
              enterKeyHint="enter"
              value={draft?.description || ''}
              onChange={(e) => setField('description', e.target.value)}
            />
            {marks.has('description') && (
              <Box position="absolute" top="8px" right="10px"><FilledMark show /></Box>
            )}
          </Box>
        </Box>
      )}
    </Box>
  );

  const saveAnother = () => save({ another: true });

  const bottomBar = mode === 'describe' ? (
    <Button
      variant="phonePrimary"
      w="100%"
      isLoading={reading}
      loadingText={t('quickAdd.describeMode.reading')}
      onClick={fillIn}
    >
      {t('quickAdd.describeMode.fillIn')}
    </Button>
  ) : (
    <>
      {maybeSaved && (
        <Box mb="8px" p="12px" borderRadius="12px" bg="redesign.bg.card" border="1px solid" borderColor="phone.alert" role="alert">
          <Text textStyle="phoneBody" fontWeight="600" color="redesign.heading">{t('quickAdd.maybeSaved.title')}</Text>
          <Text textStyle="phoneMeta" color="phone.muted" mt="2px">{t('quickAdd.maybeSaved.body')}</Text>
          <Flex gap="8px" mt="8px">
            <Button flex="1" h="44px" variant="outline" onClick={openExisting}>{t('quickAdd.maybeSaved.open')}</Button>
            <Button flex="1" h="44px" variant="outline" onClick={() => save({ another: maybeSaved.another, skipGuard: true })}>
              {t('quickAdd.maybeSaved.saveAnyway')}
            </Button>
          </Flex>
        </Box>
      )}
      {justSaved && (
        <Flex
          align="center"
          gap="10px"
          minH="48px"
          mb="8px"
          pl="14px"
          pr="4px"
          borderRadius="12px"
          bg="redesign.bg.card"
          border="1px solid"
          borderColor="redesign.border"
          color="redesign.heading"
          role="status"
        >
          {justSaved.undone ? (
            <Text textStyle="phoneBody" py="12px">{t('quickAdd.undone')}</Text>
          ) : savedLine({ name: justSaved.name, amount: justSaved.amount, onUndo: undoInSheet })}
        </Flex>
      )}
      {(saveError || (hint && !shortScreen)) && (
        <Text textStyle="phoneMeta" color={saveError ? 'phone.expense' : 'phone.muted'} mb="8px" textAlign="center">
          {saveError || hint}
        </Text>
      )}
      <Flex gap="8px" align="center">
        <Button
          variant="phonePrimary"
          flex="1"
          minW={0}
          px="12px"
          // "Agrega un nombre o una categoría" takes two lines at 320px.
          whiteSpace="normal"
          sx={{ [NARROW]: { fontSize: '16px', lineHeight: '20px' } }}
          isLoading={saving}
          // Names what is missing, and a tap goes to it: it never looks disabled.
          bg={blocker ? 'redesign.bg.accent' : failed ? 'phone.alert' : undefined}
          color={blocker ? 'redesign.text' : undefined}
          _hover={blocker ? { bg: 'redesign.bg.accent' } : undefined}
          _active={blocker ? { bg: 'redesign.bg.accent' } : undefined}
          onClick={() => save()}
        >
          <Box as="span" noOfLines={2} textAlign="center">{saveLabel}</Box>
        </Button>
        {!blocker && keyboardUp && (
          <>
            <Button
              variant="outline"
              h="52px"
              borderRadius="14px"
              color="phone.link"
              borderColor="redesign.border"
              fontSize="15px"
              px="12px"
              sx={{ [NARROW]: { display: 'none' } }}
              isDisabled={saving}
              onClick={saveAnother}
            >
              {t('quickAdd.saveAnother')}
            </Button>
            <IconButton
              icon={<Box as={LuPlus} boxSize="22px" />}
              variant="outline"
              h="52px"
              w="52px"
              borderRadius="14px"
              color="phone.link"
              borderColor="redesign.border"
              sx={{ [WIDE]: { display: 'none' } }}
              isDisabled={saving}
              aria-label={t('quickAdd.saveAnother')}
              onClick={saveAnother}
            />
          </>
        )}
      </Flex>
      {!blocker && !keyboardUp && (
        <Button
          variant="ghost"
          w="100%"
          h="44px"
          mt="4px"
          color="phone.link"
          fontSize="17px"
          fontWeight="500"
          isDisabled={saving}
          onClick={saveAnother}
        >
          {t('quickAdd.saveAnother')}
        </Button>
      )}
    </>
  );

  const pickerLeg = picker === 'from' ? 'from_account_id' : picker === 'to' ? 'to_account_id' : 'account_id';
  let pickerTitle = accountLabel;
  if (picker === 'from') pickerTitle = t('quickAdd.from');
  else if (picker === 'to') pickerTitle = t('quickAdd.to');

  return (
    <>
      <Box
        position="fixed"
        inset={0}
        zIndex={Z_INDEX - 1}
        // The scrim the theme gives every other sheet.
        bg="rgba(26, 25, 23, 0.32)"
        _dark={{ bg: 'rgba(0, 0, 0, 0.6)' }}
        opacity={isOpen ? 1 : 0}
        pointerEvents={isOpen ? 'auto' : 'none'}
        transition="opacity 0.25s"
        onClick={requestClose}
        aria-hidden="true"
      />
      <Box
        ref={sheetRef}
        // A Chakra modal's focus lock would refuse the amount's focus() when
        // quick add is opened from inside a modal or another sheet.
        data-no-focus-lock="true"
        role="dialog"
        aria-modal="true"
        aria-label={t('navigation:tabs.add')}
        position="fixed"
        left={0}
        right={0}
        bottom={0}
        top="calc(env(safe-area-inset-top) + 8px)"
        zIndex={Z_INDEX}
        bg="redesign.bg.surface"
        color="redesign.text"
        borderTopRadius="22px"
        transform={isOpen ? 'translateY(0)' : 'translateY(100%)'}
        transition="transform 0.3s cubic-bezier(0.32, 0.72, 0, 1)"
        display="flex"
        flexDirection="column"
        pl="env(safe-area-inset-left)"
        pr="env(safe-area-inset-right)"
        // The keypad covers the bottom of the layout viewport on iOS: the
        // Save bar rides on top of it.
        pb={`${inset}px`}
        // A press on a button would move focus off the field first: the
        // keypad would drop and the layout move under the finger before the
        // click lands. Focus stays until the click has run; the handlers that
        // should drop the keypad blur it themselves.
        onMouseDown={(e) => { if (pressKeepsFocus(sheetRef.current, e.target)) e.preventDefault(); }}
      >
        <Box
          position="relative"
          display="grid"
          gridTemplateColumns="1fr auto 1fr"
          alignItems="center"
          h="52px"
          px="4px"
          flexShrink={0}
        >
          <Box
            position="absolute"
            top="6px"
            left="50%"
            transform="translateX(-50%)"
            w="36px"
            h="5px"
            borderRadius="3px"
            bg="redesign.text.faint"
            aria-hidden="true"
          />
          <Flex justify="flex-start">
            <Button variant="ghost" color="redesign.text" fontSize="17px" fontWeight="400" px="12px" minH="44px" onClick={requestClose}>
              {t('common:actions.cancel')}
            </Button>
          </Flex>
          <Text textStyle="phoneHeadline" color="redesign.heading" noOfLines={1}>
            {t('navigation:tabs.add')}
          </Text>
          <Flex justify="flex-end">
            {mode === 'describe' ? (
              <Button
                variant="ghost"
                color="phone.link"
                fontSize="16px"
                fontWeight="600"
                px="12px"
                minH="44px"
                aria-label={t('quickAdd.describeMode.toAmountForm')}
                onClick={toAmountMode}
              >
                <Box as="span" fontSize="11px" fontWeight="700" border="1.5px solid" borderRadius="4px" px="3px" lineHeight="14px">
                  123
                </Box>
                <Box as="span" ml="6px" sx={{ [NARROW]: { display: 'none' } }}>{t('quickAdd.amount')}</Box>
              </Button>
            ) : (
              <Button
                variant="ghost"
                color="phone.link"
                fontSize="16px"
                fontWeight="600"
                px="12px"
                minH="44px"
                aria-label={t('quickAdd.describeAria')}
                onClick={toDescribe}
              >
                <Box as={LuSparkles} boxSize="18px" />
                {/* Icon-only below 360px, where the title needs the room. */}
                <Box as="span" ml="5px" sx={{ [NARROW]: { display: 'none' } }}>
                  {t('quickAdd.describe')}
                </Box>
              </Button>
            )}
          </Flex>
        </Box>

        <Box flex="1" minH={0} overflowY="auto" overscrollBehavior="contain" px="16px" pb="16px">
          {mode === 'describe' && (
            <Box pt="8px">
              <Textarea
                ref={describeRef}
                rows={3}
                minH="98px"
                px="14px"
                py="12px"
                lineHeight="24px"
                resize="none"
                borderRadius="14px"
                bg="redesign.bg.card"
                color="redesign.heading"
                borderColor="phone.link"
                borderWidth="1.5px"
                focusBorderColor="phone.link"
                _focus={DESCRIBE_FOCUS}
                _focusVisible={DESCRIBE_FOCUS}
                _hover={{ borderColor: 'phone.link' }}
                autoCapitalize="sentences"
                enterKeyHint="go"
                placeholder={t('quickAdd.describeMode.placeholder')}
                aria-label={t('quickAdd.describeMode.aria')}
                value={describeText}
                // Read-only, not disabled, while reading: it keeps focus, so
                // the keypad and the pinned button stay where they are.
                isReadOnly={reading}
                onChange={(e) => setDescribeText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    fillIn();
                  }
                }}
              />
              <Flex align="center" gap="6px" mt="10px" color="phone.muted">
                <Box as={LuMic} boxSize="14px" flexShrink={0} />
                <Text textStyle="phoneMeta">{t('quickAdd.describeMode.dictateHint')}</Text>
              </Flex>
              {describeError && (
                <Box mt="12px" role="alert">
                  <Text textStyle="phoneMeta" color="phone.expense">{describeError}</Text>
                  {describeOverBudget && (
                    <Text textStyle="phoneMeta" color="phone.muted" mt="4px">{t('common:aiBudget.useForm')}</Text>
                  )}
                  <Button variant="ghost" color="phone.link" px="0" h="44px" fontSize="16px" onClick={toAmountMode}>
                    {t('quickAdd.describeMode.useForm')}
                  </Button>
                </Box>
              )}
            </Box>
          )}

          {/* The amount stays mounted in both modes: it is what the + tap
              focuses, so it must always be there. */}
          <Box display={mode === 'amount' ? 'block' : 'none'}>
            {marks.size > 0 && (
              <Flex
                align="center"
                gap="8px"
                mt="4px"
                mb="8px"
                px="14px"
                py="10px"
                borderRadius="12px"
                bg="redesign.bg.accent"
                color="redesign.text"
                role="status"
              >
                <Box as={LuSparkles} boxSize="16px" color="phone.link" flexShrink={0} />
                <Text fontSize="14px" lineHeight="20px">{t('quickAdd.describeMode.banner')}</Text>
              </Flex>
            )}

            <Box
              // On a short screen with the keypad up the type gives way to the
              // name and its chips; the Save button still names it.
              display={shortScreen ? 'none' : 'grid'}
              gridTemplateColumns="repeat(3, minmax(0, 1fr))"
              // Below 360px each segment takes what its label needs, so
              // "Transferencia" fits beside "Gasto" and "Ingreso".
              sx={{ [NARROW]: { gridTemplateColumns: 'repeat(3, auto)' } }}
              mt="4px"
              borderRadius="12px"
              bg="redesign.bg.sunken"
              border="1px solid"
              borderColor="redesign.border"
              role="radiogroup"
              aria-label={t('quickAdd.typeAria')}
            >
              {TYPES.map((opt) => {
                const on = opt.value === type;
                return (
                  <Box
                    key={opt.value}
                    as="button"
                    type="button"
                    role="radio"
                    aria-checked={on}
                    onClick={() => changeType(opt.value)}
                    h="44px"
                    minW={0}
                    p="3px"
                  >
                    <Flex
                      h="100%"
                      align="center"
                      justify="center"
                      gap="4px"
                      borderRadius="9px"
                      // Raised above the track, as a chosen segment reads.
                      bg={on ? 'redesign.bg.raised' : 'transparent'}
                      boxShadow={on ? 'sm' : 'none'}
                      color={on ? 'redesign.heading' : 'phone.muted'}
                      px="6px"
                      fontSize="14px"
                      fontWeight="500"
                    >
                      <Box as="span" noOfLines={1}>{t(opt.labelKey)}</Box>
                      <FilledMark show={on && marks.has('type')} size={12} />
                    </Flex>
                  </Box>
                );
              })}
            </Box>

            <Flex align="center" justify="space-between" gap="12px" h={shortScreen ? '60px' : '80px'} mt="4px">
              <Box
                as="button"
                type="button"
                onClick={() => openPicker('currency')}
                display="inline-flex"
                alignItems="center"
                gap="4px"
                flexShrink={0}
                h="44px"
                pl="14px"
                pr="10px"
                borderRadius="22px"
                bg="redesign.bg.card"
                border="1px solid"
                borderColor="redesign.border"
                color="redesign.heading"
                fontSize="16px"
                fontWeight="600"
                aria-label={t('quickAdd.currencyAria', { code: draft?.currency || '' })}
              >
                {draft?.currency}
                <Box as={LuChevronDown} boxSize="16px" color="phone.muted" />
              </Box>
              <Flex align="center" minW={0} flex="1" justify="flex-end" gap="4px">
                <Input
                  ref={amountRef}
                  id="quick-add-amount"
                  type="text"
                  inputMode="decimal"
                  autoComplete="off"
                  data-display-size=""
                  variant="unstyled"
                  textStyle="phoneAmountInput"
                  fontSize="40px"
                  h="56px"
                  minW={0}
                  textAlign="right"
                  color="redesign.heading"
                  letterSpacing="-0.02em"
                  placeholder="0"
                  _placeholder={{ color: 'redesign.text.faint' }}
                  value={amountFocused ? amountText : groupForDisplay(amountText)}
                  onFocus={() => setAmountFocused(true)}
                  onBlur={() => setAmountFocused(false)}
                  onChange={(e) => setField('amount', e.target.value.replace(/[^0-9.,]/g, ''))}
                  aria-label={t('quickAdd.amount')}
                />
                <FilledMark show={marks.has('amount')} size={16} />
              </Flex>
            </Flex>

            <Flex
              align="center"
              h="52px"
              mt="4px"
              pl="14px"
              pr="4px"
              borderRadius="12px"
              bg="redesign.bg.card"
              border="1px solid"
              borderColor="redesign.border"
            >
              <Box position="relative" flex="1" minW={0} h="50px">
                <Input
                  ref={nameRef}
                  // No "name" in the id: it invites iOS contact AutoFill.
                  id="quick-add-what"
                  name="quick-add-what"
                  variant="unstyled"
                  enterKeyHint="done"
                  autoCorrect="off"
                  autoComplete="off"
                  placeholder={t('quickAdd.namePlaceholder')}
                  _placeholder={{ color: 'phone.muted' }}
                  color="redesign.heading"
                  h="50px"
                  w="100%"
                  pr={marks.has('note') ? '22px' : 0}
                  value={draft?.note || ''}
                  onChange={(e) => setField('note', e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                  aria-label={t('quickAdd.nameAria')}
                />
                {/* The ✨ right after the text: an invisible copy of the name
                    takes its width. */}
                {marks.has('note') && (
                  <Flex position="absolute" inset={0} align="center" pointerEvents="none" overflow="hidden">
                    <Box as="span" visibility="hidden" whiteSpace="pre" fontSize="16px" minW={0} maxW="calc(100% - 22px)" overflow="hidden">
                      {draft?.note}
                    </Box>
                    <Box ml="6px" flexShrink={0}><FilledMark show /></Box>
                  </Flex>
                )}
              </Box>
              {draft?.note ? (
                <IconButton
                  icon={(
                    <Flex w="20px" h="20px" borderRadius="full" align="center" justify="center" bg="redesign.text.faint">
                      <Box as={LuX} boxSize="12px" strokeWidth={3} />
                    </Flex>
                  )}
                  variant="ghost"
                  color="redesign.text"
                  minW="44px"
                  minH="44px"
                  flexShrink={0}
                  aria-label={t('quickAdd.clearName')}
                  onClick={() => { setField('note', ''); lookedUp.current = ''; }}
                />
              ) : <Box w="4px" />}
            </Flex>

            {chips.length > 0 && (
              <Box position="relative" mt="8px" mx="-16px">
                <Flex gap="8px" px="16px" overflowX="auto" sx={{ scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' } }}>
                  {chips.map((note) => (
                    <Chip key={note} onClick={() => pickNote(note)} fontSize="15px">{note}</Chip>
                  ))}
                  <Box flexShrink={0} w="40px" />
                </Flex>
                <Box
                  position="absolute"
                  right={0}
                  top={0}
                  bottom={0}
                  w="48px"
                  pointerEvents="none"
                  bgGradient="linear(to-r, transparent, redesign.bg.surface 80%)"
                />
              </Box>
            )}

            {showDefaultsLine && (
              <Box
                as="button"
                type="button"
                onClick={showDetails}
                display="flex"
                alignItems="center"
                gap="6px"
                w="100%"
                h="44px"
                mt="4px"
                color="phone.muted"
                textAlign="left"
              >
                <AccountIcon account={isTransfer ? fromAccount : account} size={16} />
                <Text textStyle="phoneMeta" noOfLines={1}>{defaultsLine}</Text>
                <Box as={LuChevronRight} boxSize="14px" flexShrink={0} />
              </Box>
            )}

            <Box
              ref={detailsRef}
              display={hideDetails ? 'none' : 'block'}
              pt="12px"
              style={{ scrollMarginTop: '8px' }}
              onFocusCapture={() => setDetailsFocused(true)}
              onBlurCapture={() => setDetailsFocused(false)}
            >
              {accountField}
              {transferFields}
              {categoryField}
              {dateField}
              {moreDetails}
            </Box>
          </Box>
        </Box>

        <Box
          flexShrink={0}
          px="16px"
          pt="8px"
          pb={keyboardUp ? '8px' : 'calc(env(safe-area-inset-bottom) + 8px)'}
          bg="redesign.bg.surface"
        >
          {bottomBar}
        </Box>
      </Box>

      <AccountSheet
        isOpen={picker === 'account' || picker === 'from' || picker === 'to'}
        onClose={() => setPicker(null)}
        title={pickerTitle}
        accounts={accounts}
        recentIds={recentAccountIds()}
        value={draft?.[pickerLeg]}
        onChange={(acc) => chooseAccount(pickerLeg, acc)}
      />
      <CategorySheet
        isOpen={picker === 'category'}
        onClose={() => setPicker(null)}
        categories={categories}
        type={type}
        value={draft?.category_id}
        // No recent row: the tiles under the sheet are already the most used.
        onChange={(c) => setField('category_id', c?.id ?? null)}
      />
      <TagSheet
        isOpen={picker === 'tags'}
        onClose={() => setPicker(null)}
        tags={tags}
        value={tagIds}
        onChange={(ids, chosen) => setField('tag_movements', chosen.map((tag) => ({ tag: { id: tag.id, name: tag.name } })))}
      />
      <CurrencySheet
        isOpen={picker === 'currency'}
        onClose={() => setPicker(null)}
        currencies={currencies}
        value={draft?.currency}
        onChange={(code) => setField('currency', code)}
      />

      <PhoneConfirmSheet
        isOpen={confirmOpen}
        title={t('common:confirm.discardMovementTitle')}
        confirmLabel={t('common:confirm.discard')}
        cancelLabel={t('common:confirm.keepEditing')}
        destructive
        onConfirm={() => {
          setConfirmOpen(false);
          clearDraft();
          guardNext.current = false;
          close();
        }}
        onCancel={() => setConfirmOpen(false)}
      />
    </>
  );
});

export default QuickAddSheet;
